import type { RouteTable } from "@hakili/app-shell";

/**
 * `P0-DLW-002`: dealer-web's route table, which `P0-DLW-001` deliberately did
 * not build.
 *
 * Its `App` said why: *"One screen behind a gate needs no route table, and a
 * table built now would be a guess about Round C's shape."* That was right then
 * and stops being right here, because the second route is not a screen a
 * dealership's people navigate to , it is the one they ARRIVE on, from a link
 * in a mailbox, with no session at all.
 *
 * **Two routes, and the second is public.** `00-SPINE` 6.3 and 6.3.6's rule for
 * `/depot/:token`: the token IS the authorisation, so `AuthGate` must let its
 * holder through rather than send them to a login whose password this very
 * screen exists to set.
 *
 * The team screen and the rail arrive in Round C, and they arrive HERE , which
 * is the whole reason a table is worth more than an `if` on the path.
 */
export const ROUTES: RouteTable = {
  routes: [
    { path: "/", screen: "accueil" },
    // `P1-DLW-051` (DESIGNER R, #893): the address the shell names as the landing; it is the SAME screen and it lights « Accueil ».
    { path: "/accueil", screen: "accueil" },
    // `P0-DLW-006`. The PATH stays French because a URL is product surface, like
    // the copy beside it , the founder's decision of 5 September 2026 is about
    // the names a DEVELOPER reads, and `fr-ML` is unchanged (06 6.5.1). The
    // screen behind it is `Team`, in English, like every new name in this tree.
    { path: "/equipe", screen: "team" },
    // `P0-PTA-001`. Same rule as « Équipe »: the PATH is French because a URL is
    // product surface; the screen behind it is `Profile`, in English.
    // `P0-DLW-010`. The list, and the car's own page , which stays its OWN
    // route so a deep link keeps working: the wide-screen split renders the
    // same component beside the list, and a split that swallowed the address
    // would break every link already sent.
    { path: "/vehicules", screen: "vehicles" },
    { path: "/vehicules/:id", screen: "vehicles" },
    // `P1-DLW-048` (G-E1): the dealership's dossier list. The PATH is French, the screen is `Dossiers`.
    { path: "/dossiers", screen: "dossiers" },
    // `P1-DLW-054` (PROSPECTS): the entry and the route arrive together.
    { path: "/prospects", screen: "prospects" },
    // `P1-DLW-052` (B5-b): the dossier's own page. The same screen name as the list: the rail lights « Dossiers » on both, and a deep link keeps working.
    { path: "/dossiers/:id", screen: "dossiers" },
    /**
     * `round F`, `F1`: the rail's remaining three, seated with the entries that
     * point at them.
     *
     * **The ENTRY and the ROUTE arrive together, always.** A rail entry whose
     * path resolves to nothing falls through to `landingScreen`, so a person
     * pressing « Solde » would be told, silently, that he had pressed
     * « Tableau de bord » , the worst kind of broken, because it looks like
     * working. The screens behind them are `F5`'s; until `F5` lands they are
     * `EcranAVenir`, present and honest, which is `P1-UIK-025`'s convention.
     */
    // `DEALER-PREQUAL` (round A-D1, D-1031-18): « Estimer un financement ». The PATH is French (product surface); the entry moves to the Comptoir screen when that is built.
    { path: "/estimation", screen: "estimation" },
    { path: "/rendez-vous", screen: "appointments" },
    { path: "/solde", screen: "balance" },
    { path: "/fiabilite", screen: "reliability" },
    { path: "/profil", screen: "profile" },
    { path: "/invitation/:token", screen: "invitation", public: true },
  ],
  landingScreen: "accueil",
};

/** The screens a signed-in dealership user may reach. `invitation` is NOT one: it is public. */
export const ECRANS: ReadonlySet<string> = new Set([
  "accueil",
  "team",
  "profile",
  "vehicles",
  "estimation",
  "dossiers",
  "prospects",
  "appointments",
  "balance",
  "reliability",
]);
